import React, { useState } from 'react';

import { Button, Group, SegmentedControl, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import type { MovementWithSection } from 'components/Leaderboard/movementLabel';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import type {
  LeaderboardGenderFilter,
  LeaderboardLevelFilter,
} from 'hooks/workoutLeaderboard/useWorkoutLeaderboard';
import { WORKOUT_LEVEL_LABEL_MAP, WorkoutLevelEnum } from 'types/enums';

export interface LeaderboardAppliedFilters {
  session: 'this-session' | 'today';
  gender: LeaderboardGenderFilter;
  level: LeaderboardLevelFilter;
}

// Ascending difficulty, matching LevelPicker's own display order.
const LEVEL_DISPLAY_ORDER = [WorkoutLevelEnum.SCALED, WorkoutLevelEnum.RX, WorkoutLevelEnum.RX_PLUS];

// Only the levels the section actually offers appear here, same rule LevelPicker
// itself uses — plus an "All" option, which a plain level picker has no use for.
const buildLevelOptions = (
  movement: MovementWithSection,
): Array<{ value: LeaderboardLevelFilter; label: string }> => {
  const availability: Record<WorkoutLevelEnum, boolean> = {
    [WorkoutLevelEnum.SCALED]: movement.levelScaled,
    [WorkoutLevelEnum.RX]: movement.levelRx,
    [WorkoutLevelEnum.RX_PLUS]: movement.levelRxPlus,
  };
  const offeredLevels = LEVEL_DISPLAY_ORDER.filter(level => availability[level]).map(level => ({
    value: level as LeaderboardLevelFilter,
    label: WORKOUT_LEVEL_LABEL_MAP[level],
  }));
  return offeredLevels.length === 0 ? [] : [{ value: 'all', label: 'All' }, ...offeredLevels];
};

interface LeaderboardFilterSheetProps {
  movement: MovementWithSection;
  appliedFilters: LeaderboardAppliedFilters;
  onApply: (filters: LeaderboardAppliedFilters) => void;
  onCancel: () => void;
}

const LeaderboardFilterSheet = ({
  movement,
  appliedFilters,
  onApply,
  onCancel,
}: LeaderboardFilterSheetProps): JSX.Element => {
  const [pending, setPending] = useState<LeaderboardAppliedFilters>(appliedFilters);
  const levelOptions = buildLevelOptions(movement);
  const {
    trackLeaderboardDateFilterChanged,
    trackLeaderboardGenderFilterChanged,
    trackLeaderboardCategoryFilterChanged,
    trackLeaderboardFilterSheetApplied,
  } = useStaffTracking();

  return (
    <Stack spacing='lg'>
      <Group spacing='xs'>
        <UnstyledButton
          onClick={onCancel}
          sx={theme => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: theme.colors.gray[1],
          })}
        >
          <IconArrowLeft size={16} />
        </UnstyledButton>
        <Text weight={700}>Filter by</Text>
      </Group>

      <Stack spacing={6}>
        <Text size='xs' weight={700} color='dimmed' transform='uppercase'>
          Date
        </Text>
        <SegmentedControl
          fullWidth
          value={pending.session}
          onChange={value => {
            const session = value as LeaderboardAppliedFilters['session'];
            setPending(prev => ({ ...prev, session }));
            trackLeaderboardDateFilterChanged({ value: session });
          }}
          data={[
            { value: 'this-session', label: 'This Session' },
            { value: 'today', label: 'Today' },
          ]}
        />
      </Stack>

      <Stack spacing={6}>
        <Text size='xs' weight={700} color='dimmed' transform='uppercase'>
          Gender
        </Text>
        <SegmentedControl
          fullWidth
          value={pending.gender}
          onChange={value => {
            const gender = value as LeaderboardGenderFilter;
            setPending(prev => ({ ...prev, gender }));
            trackLeaderboardGenderFilterChanged({ value: gender });
          }}
          data={[
            { value: 'all', label: 'All' },
            { value: 'men', label: 'Men' },
            { value: 'women', label: 'Women' },
          ]}
        />
      </Stack>

      {levelOptions.length > 0 && (
        <Stack spacing={6}>
          <Text size='xs' weight={700} color='dimmed' transform='uppercase'>
            Category
          </Text>
          <SegmentedControl
            fullWidth
            value={pending.level}
            onChange={value => {
              const level = value as LeaderboardLevelFilter;
              setPending(prev => ({ ...prev, level }));
              trackLeaderboardCategoryFilterChanged({ value: level });
            }}
            data={levelOptions}
          />
        </Stack>
      )}

      <Stack spacing='xs'>
        <Button
          fullWidth
          onClick={() => {
            trackLeaderboardFilterSheetApplied({
              date: pending.session,
              gender: pending.gender,
              level: pending.level,
            });
            onApply(pending);
          }}
        >
          Show Results
        </Button>
        <Button fullWidth variant='subtle' color='gray' onClick={onCancel}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default LeaderboardFilterSheet;
